import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api, { tCrit, CRITICALITY, RESILIENCE_CLS, RESILIENCE_FILTERS, RESILIENCE_FILTER_KEYS, tTestResult } from "@/lib/api";
import { CritBadge, StatusBadge } from "@/components/shared/Badges";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Activity, ShieldAlert, HelpCircle, Server, Boxes, AlertTriangle, Loader2, ChevronDown,
  Layers, CheckCircle2, GitCompare,
} from "lucide-react";

const FRAGILITY_DOT = {
  "Élevée": "#A1A1AA", "Modérée": "#A1A1AA", "Faible": "#A1A1AA", "Données insuffisantes": "#71717A",
};

function DomainTags({ list, className = "" }) {
  if (!list?.length) return null;
  return (
    <span className={`inline-flex flex-wrap items-center gap-1 ${className}`}>
      {list.map((c) => (
        <Badge key={c.id} variant="outline" className="rounded-sm text-[10px] border-yellow-500/40 text-yellow-300">{c.type_label} : {c.name}</Badge>
      ))}
    </span>
  );
}

function GraphNode({ n, onNavigate }) {
  const isService = n.kind === "service";
  const dot = CRITICALITY[n.criticality]?.dot || "#71717A";
  const Icon = isService ? Boxes : Server;
  const border = n.is_spof ? "#FF9F0A" : isService ? "#0066FF" : dot;
  return (
    <button
      onClick={() => !isService && onNavigate(n.id)}
      className={`relative px-3 py-2 rounded-sm text-left min-w-[150px] max-w-[200px] transition-transform hover:-translate-y-0.5 ${isService ? "bg-primary/15 border-2" : "bg-background border"} ${n.kind === "indirect" ? "border-dashed" : ""} ${n.kind === "alternative" ? "border-dashed border-emerald-500/60" : ""}`}
      style={{ borderColor: n.kind === "alternative" ? undefined : border + (n.is_spof ? "" : "80") }}
      data-testid={`impact-node-${n.id}`}
      title={isService ? n.name : "Ouvrir la fiche de l'actif"}
    >
      <div className="flex items-center gap-2">
        <span className="h-2 w-2 rounded-full shrink-0" style={{ background: dot }} />
        <Icon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
        <span className={`text-xs truncate ${isService ? "font-semibold" : "font-medium"}`}>{n.name}</span>
      </div>
      <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mt-1 pl-4">
        {isService ? "service" : n.kind === "direct" ? "directe" : n.kind === "alternative" ? "alternative" : "indirecte"}
      </div>
      {n.failure_domains?.length > 0 && (
        <div className="mt-1 pl-4 flex items-center gap-1 text-[9px] text-yellow-400/80">
          <Layers className="h-2.5 w-2.5" /> {n.failure_domains.map((d) => d.name).join(", ")}
        </div>
      )}
      {n.is_spof && (
        <span className="absolute -top-2 -right-2 flex items-center gap-1 px-1.5 py-0.5 rounded-sm bg-orange-500 text-black text-[9px] font-bold uppercase">
          <ShieldAlert className="h-2.5 w-2.5" /> SPOF
        </span>
      )}
    </button>
  );
}

function Viz({ graph, onNavigate }) {
  const nodes = graph.nodes || [];
  const service = nodes.find((n) => n.kind === "service");
  const direct = nodes.filter((n) => n.kind === "direct");
  const alternative = nodes.filter((n) => n.kind === "alternative");
  const indirect = nodes.filter((n) => n.kind === "indirect");
  return (
    <div className="flex flex-col items-center gap-3" data-testid="impact-viz">
      {service && <GraphNode n={service} onNavigate={onNavigate} />}
      {direct.length > 0 && <div className="h-5 w-px bg-border" />}
      <div className="flex flex-wrap justify-center gap-3">
        {direct.map((n) => <GraphNode key={n.id} n={n} onNavigate={onNavigate} />)}
      </div>
      {alternative.length > 0 && (
        <>
          <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-400/70">Alternatives</div>
          <div className="flex flex-wrap justify-center gap-3">
            {alternative.map((n) => <GraphNode key={n.id} n={n} onNavigate={onNavigate} />)}
          </div>
        </>
      )}
      {indirect.length > 0 && (
        <>
          <div className="h-5 w-px bg-border" />
          <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Dépendances indirectes</div>
          <div className="flex flex-wrap justify-center gap-3">
            {indirect.map((n) => <GraphNode key={n.id} n={n} onNavigate={onNavigate} />)}
          </div>
        </>
      )}
    </div>
  );
}

function Why({ row }) {
  const [open, setOpen] = useState(false);
  const lines = [
    `Criticité : ${tCrit(row.criticality) || "—"}`,
    `Relation : ${row.relation_label}`,
    `Dépendance ${row.dependency_kind}`,
    row.via ? `Chemin : ${row.via.join(" → ")}` : null,
    row.resilience_label ? `Qualification : ${row.resilience_label}` : null,
    row.alt_confidence ? `Confiance de l'alternative : ${row.alt_confidence}` : null,
    row.common_domains?.length ? `Domaine(s) de défaillance commun(s) : ${row.common_domains.map((c) => `${c.type_label} : ${c.name}`).join("; ")}` : null,
    row.best_last_test ? `Dernier test de bascule : ${tTestResult(row.best_last_test.result)}${row.best_last_test.tested_at ? " le " + new Date(row.best_last_test.tested_at).toLocaleDateString("fr-FR") : ""}` : null,
    row.spof_reason || (row.is_spof ? "Point de défaillance unique potentiel." : null),
    `${row.dependents_count || 0} composant(s) aval en dépendent également`,
  ].filter(Boolean);
  return (
    <div>
      <Button variant="ghost" size="sm" className="h-7 gap-1 text-xs" onClick={() => setOpen(!open)} data-testid={`why-${row.id}`}>
        <HelpCircle className="h-3.5 w-3.5" /> Pourquoi ? <ChevronDown className={`h-3 w-3 transition-transform ${open ? "rotate-180" : ""}`} />
      </Button>
      {open && (
        <ul className="mt-2 space-y-1 text-[11px] text-muted-foreground border-l-2 border-border pl-3">
          {lines.map((l) => <li key={l}>{l}</li>)}
        </ul>
      )}
    </div>
  );
}

function DepTable({ rows, kind, onNavigate }) {
  if (!rows.length) return <p className="text-sm text-muted-foreground">Aucune dépendance {kind}.</p>;
  const showResil = kind === "directe";
  return (
    <div className="border border-border rounded-sm overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent border-border">
            <TableHead>Actif</TableHead>
            <TableHead>Relation</TableHead>
            <TableHead>Criticité</TableHead>
            {showResil && <TableHead>Résilience structurelle</TableHead>}
            <TableHead>Statut</TableHead>
            <TableHead>Explicabilité</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.id} className="border-border" data-testid={`impact-row-${r.id}`}>
              <TableCell className="font-medium">
                <button className="flex items-center gap-2 hover:text-primary" onClick={() => onNavigate(r.id)}>
                  <Server className="h-3.5 w-3.5 text-muted-foreground" /> {r.name}
                  {r.is_spof && <Badge className="rounded-sm bg-orange-500/15 text-orange-400 border border-orange-500/30 text-[10px]">SPOF potentiel</Badge>}
                </button>
                {r.failure_domains?.length > 0 && <div className="mt-1"><DomainTags list={r.failure_domains} /></div>}
              </TableCell>
              <TableCell><Badge variant="outline" className="rounded-sm font-mono text-xs">{r.relation_label}</Badge></TableCell>
              <TableCell>{r.criticality ? <CritBadge value={r.criticality} /> : "—"}</TableCell>
              {showResil && (
                <TableCell>
                  <span className={`text-xs font-medium ${r.is_spof ? "text-orange-400" : RESILIENCE_CLS[r.resilience_status] || "text-muted-foreground"}`} data-testid={`resilience-${r.id}`}>
                    {r.resilience_label || "—"}
                  </span>
                  {r.alt_confidence && <span className="block text-[10px] text-muted-foreground">Confiance : {r.alt_confidence}</span>}
                  {r.common_domains?.length > 0 && <div className="mt-1"><DomainTags list={r.common_domains} /></div>}
                  {r.best_last_test && <span className="block text-[10px] mt-0.5">Dernier test : <span className={r.best_last_test.result === "reussi" ? "text-green-400" : r.best_last_test.result === "echoue" ? "text-red-400" : "text-yellow-400"}>{tTestResult(r.best_last_test.result)}</span></span>}
                </TableCell>
              )}
              <TableCell>{r.status ? <StatusBadge value={r.status} /> : "—"}</TableCell>
              <TableCell><Why row={r} /></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function Panel({ tone, icon: Icon, title, items, onNavigate, testid }) {
  if (!items?.length) return null;
  const cls = {
    orange: "border-orange-500/30 bg-orange-500/5 text-orange-400",
    green: "border-green-500/30 bg-green-500/5 text-green-400",
    yellow: "border-yellow-500/30 bg-yellow-500/5 text-yellow-400",
  }[tone];
  return (
    <div className={`rounded-sm border p-5 ${cls}`} data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className="h-4 w-4" />
        <span className="font-head font-semibold">{title} — {items.length}</span>
      </div>
      <ul className="space-y-2">
        {items.map((s) => (
          <li key={s.id} className="text-sm" data-testid={`${testid}-${s.id}`}>
            <button className="font-medium hover:underline" onClick={() => onNavigate(s.id)}>{s.name}</button>
            <span className="text-xs text-muted-foreground"> — {s.reason}</span>
            {s.common_domains?.length > 0 && <div className="mt-1"><DomainTags list={s.common_domains} /></div>}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function ImpactAnalysis({ serviceId, onNavigate }) {
  const [selectedResil, setSelectedResil] = useState(() => [...RESILIENCE_FILTER_KEYS]);
  const toggleResil = (r) => setSelectedResil((s) => s.includes(r) ? s.filter((x) => x !== r) : [...s, r]);
  const { data, isLoading } = useQuery({
    queryKey: ["service-impact", serviceId],
    queryFn: async () => (await api.get(`/services/${serviceId}/impact`)).data,
  });

  if (isLoading)
    return <div className="flex items-center gap-2 text-muted-foreground font-mono text-sm"><Loader2 className="h-4 w-4 animate-spin" /> Analyse en cours…</div>;
  if (!data) return null;

  const allResil = selectedResil.length === RESILIENCE_FILTER_KEYS.length;
  const directRows = allResil ? data.direct : data.direct.filter((r) => selectedResil.includes(r.resilience_filter));
  const f = data.fragility;
  return (
    <div className="space-y-6" data-testid="impact-analysis">
      <div className="rounded-sm border border-border bg-secondary/30 p-5 text-foreground" data-testid="fragility-indicator">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Activity className="h-4 w-4 text-muted-foreground" />
            <span className="text-xs font-mono uppercase tracking-[0.15em] text-muted-foreground">Fragilité structurelle</span>
          </div>
          <span className="flex items-center gap-2 font-head text-lg font-bold px-2.5 py-0.5 rounded-sm bg-background border border-border" data-testid="fragility-level">
            <span className="h-2 w-2 rounded-full" style={{ background: FRAGILITY_DOT[f.level] || "#71717A" }} />
            {f.level}
          </span>
        </div>
        <ul className="mt-3 space-y-1 text-xs text-muted-foreground">
          {f.criteria.map((c) => <li key={c}>• {c}</li>)}
        </ul>
        <p className="mt-3 text-[11px] italic text-muted-foreground/70">{f.note}</p>
      </div>

      <Panel tone="orange" icon={AlertTriangle} title="Points de défaillance unique potentiels" items={data.spofs} onNavigate={onNavigate} testid="spof-list" />
      <Panel tone="yellow" icon={GitCompare} title="Redondances partielles (domaine de défaillance commun)" items={data.partial_redundancies} onNavigate={onNavigate} testid="partial-list" />
      <Panel tone="green" icon={CheckCircle2} title="Redondances vérifiées" items={data.verified_redundancies} onNavigate={onNavigate} testid="verified-list" />

      <div className="border border-border bg-[#0b0b0d] rounded-sm p-6">
        <Viz graph={data.graph} onNavigate={onNavigate} />
        <div className="flex flex-wrap gap-4 mt-6 pt-4 border-t border-border text-[11px] text-muted-foreground">
          <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-sm border-2 border-primary" /> Service</span>
          <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-sm border border-blue-400" /> Dépendance directe</span>
          <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-sm border border-dashed border-emerald-500" /> Alternative</span>
          <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-sm border border-dashed border-zinc-400" /> Dépendance indirecte</span>
          <span className="flex items-center gap-1.5"><Layers className="h-3 w-3 text-yellow-400" /> Domaine de défaillance</span>
          <span className="flex items-center gap-1.5"><ShieldAlert className="h-3 w-3 text-orange-400" /> SPOF potentiel</span>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <h3 className="font-head font-semibold">Dépendances directes — {directRows.length}{!allResil ? ` / ${data.direct.length}` : ""}</h3>
          <div className="flex flex-wrap items-center gap-1.5" data-testid="impact-resilience-filter">
            {RESILIENCE_FILTER_KEYS.map((k) => {
              const on = selectedResil.includes(k);
              const v = RESILIENCE_FILTERS[k];
              return (
                <button key={k} onClick={() => toggleResil(k)} data-testid={`impact-resil-chip-${k}`}
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm border text-[11px] transition-colors ${on ? "text-white" : "text-muted-foreground opacity-50"}`}
                  style={{ borderColor: v.dot + "80", background: on ? v.dot + "22" : "transparent" }}>
                  <span className="h-1.5 w-1.5 rounded-full" style={{ background: v.dot }} /> {v.label}
                </button>
              );
            })}
            <button onClick={() => setSelectedResil([...RESILIENCE_FILTER_KEYS])} className="text-[11px] text-primary ml-0.5" data-testid="impact-resil-all">Tout</button>
          </div>
        </div>
        <DepTable rows={directRows} kind="directe" onNavigate={onNavigate} />
      </div>
      <div>
        <h3 className="font-head font-semibold mb-3">Dépendances indirectes — {data.indirect.length}</h3>
        <DepTable rows={data.indirect} kind="indirecte" onNavigate={onNavigate} />
      </div>
    </div>
  );
}
